import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Filter, CheckSquare } from 'lucide-react';
import { get, post, put, del } from '@/shared/api/api_client';
import { useAuth } from '@/features/auth';
import { Navbar } from '@/widgets/Navbar';
import { ChoreCard } from '@/widgets/ChoreCard';
import { Button, Card, Input, Modal, Badge } from '@/shared/ui';
import ScheduleChore from '@/features/chores/ScheduleChore/ScheduleChore';
import { cn } from '@/shared/utils/cn';
import { formatDate } from '@/shared/utils/format';
import type { Chore, ChoreCompletion, ChoreSchedule } from '@/entities/chore';
import type { FamilyMember } from '@/entities/user';

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------
interface ChoresResponse {
  chores: Chore[];
}

interface TodayChoresResponse {
  chores: Chore[];
  completions: ChoreCompletion[];
}

interface CompletionsResponse {
  completions: ChoreCompletion[];
}

interface FamilyMembersResponse {
  members: FamilyMember[];
}

// ---------------------------------------------------------------------------
// Chore form schema
// ---------------------------------------------------------------------------
const choreSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(60, 'Max 60 characters'),
  description: z.string().max(200, 'Max 200 characters').optional(),
  pointValue: z
    .number({ invalid_type_error: 'Must be a number' })
    .int('Must be a whole number')
    .min(1, 'At least 1 point')
    .max(10000, 'Max 10,000 points'),
  assignedTo: z.string().min(1, 'Select who this chore is for'),
  category: z.string().max(40).optional(),
});

type ChoreFormValues = z.infer<typeof choreSchema>;

// ---------------------------------------------------------------------------
// Chore form modal
// ---------------------------------------------------------------------------
function ChoreFormModal({
  isOpen,
  onClose,
  initialValues,
  initialSchedule,
  choreId,
  familyId,
  members,
}: {
  isOpen: boolean;
  onClose: () => void;
  initialValues?: Partial<ChoreFormValues>;
  initialSchedule?: ChoreSchedule | null;
  choreId?: string;
  familyId: string;
  members: FamilyMember[];
}): React.ReactElement {
  const queryClient = useQueryClient();
  const isEditing = Boolean(choreId);
  const [schedule, setSchedule] = useState<ChoreSchedule | null>(initialSchedule ?? null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    reset,
  } = useForm<ChoreFormValues>({
    resolver: zodResolver(choreSchema),
    defaultValues: {
      pointValue: initialValues?.pointValue ?? 10,
      assignedTo: initialValues?.assignedTo ?? 'any',
      ...initialValues,
    },
  });

  const onSubmit = async (data: ChoreFormValues) => {
    try {
      const payload = { ...data, schedule: schedule ?? null };
      if (isEditing && choreId) {
        await put<void>(`/families/${familyId}/chores/${choreId}`, payload);
      } else {
        await post<void>(`/families/${familyId}/chores`, payload);
      }
      void queryClient.invalidateQueries({ queryKey: ['chores', familyId] });
      void queryClient.invalidateQueries({ queryKey: ['chores-today', familyId] });
      reset();
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save chore.';
      setError('root', { message });
    }
  };

  const children = members.filter((m) => m.role === 'child');

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEditing ? 'Edit Chore' : 'Add a Chore'}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
        <Input
          label="Chore name"
          placeholder="e.g. Clean your room"
          autoFocus
          error={errors.name?.message}
          {...register('name')}
        />
        <Input
          label="Description (optional)"
          placeholder="Any extra details..."
          error={errors.description?.message}
          {...register('description')}
        />
        <div className="flex gap-3">
          <div className="flex-1 flex flex-col gap-1">
            <label className="text-sm font-medium text-slate-700" htmlFor="pointValue">
              Point value
            </label>
            <input
              id="pointValue"
              type="number"
              min={1}
              max={10000}
              className={cn(
                'block w-full rounded-lg border px-3 py-2 text-sm text-slate-900',
                'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                errors.pointValue ? 'border-danger-400' : 'border-slate-300',
              )}
              {...register('pointValue', { valueAsNumber: true })}
            />
            {errors.pointValue && (
              <p className="text-xs text-danger-600">{errors.pointValue.message}</p>
            )}
          </div>
          <div className="flex-1 flex flex-col gap-1">
            <label className="text-sm font-medium text-slate-700" htmlFor="category">
              Category (optional)
            </label>
            <input
              id="category"
              type="text"
              placeholder="e.g. Cleaning"
              className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              {...register('category')}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-slate-700" htmlFor="assignedTo">
            Assign to
          </label>
          <select
            id="assignedTo"
            className={cn(
              'block w-full rounded-lg border px-3 py-2 text-sm text-slate-900 bg-white',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
              errors.assignedTo ? 'border-danger-400' : 'border-slate-300',
            )}
            {...register('assignedTo')}
          >
            <option value="any">Any child</option>
            {children.map((child) => (
              <option key={child.memberId} value={child.memberId}>
                {child.displayName}
              </option>
            ))}
          </select>
          {errors.assignedTo && (
            <p className="text-xs text-danger-600">{errors.assignedTo.message}</p>
          )}
        </div>

        {/* Schedule */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">Schedule</h3>
          <ScheduleChore schedule={schedule} onChange={setSchedule} />
        </div>

        {errors.root && (
          <p role="alert" className="text-xs text-danger-600">{errors.root.message}</p>
        )}

        <div className="flex gap-2 pt-2">
          <Button type="submit" isLoading={isSubmitting} fullWidth>
            {isEditing ? 'Save Changes' : 'Add Chore'}
          </Button>
          <Button variant="ghost" onClick={onClose} type="button">
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// ChoresPage
// ---------------------------------------------------------------------------
export default function ChoresPage(): React.ReactElement {
  const { appUser } = useAuth();
  const queryClient = useQueryClient();

  const familyId = appUser?.familyId ?? '';
  const role = appUser?.role ?? 'child';
  const childId = appUser?.uid ?? '';

  const [activeTab, setActiveTab] = useState<'today' | 'history' | 'active' | 'all'>(
    role === 'parent' ? 'active' : 'today'
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [editChore, setEditChore] = useState<Chore | null>(null);
  const [filterChildId, setFilterChildId] = useState<string>('all');

  // Queries
  const { data: choresData, isLoading: choresLoading } = useQuery<ChoresResponse>({
    queryKey: ['chores', familyId],
    queryFn: () => get<ChoresResponse>(`/families/${familyId}/chores`),
    enabled: Boolean(familyId && role === 'parent'),
  });

  // Children use the schedule-aware today endpoint (returns { chores, completions })
  const { data: todayData, isLoading: todayLoading } = useQuery<TodayChoresResponse>({
    queryKey: ['chores-today', familyId, childId],
    queryFn: () => get<TodayChoresResponse>(`/families/${familyId}/chores/today`),
    enabled: Boolean(familyId && role === 'child'),
  });

  // Child's own completion history (child-accessible endpoint)
  const { data: completionsData, isLoading: completionsLoading } = useQuery<CompletionsResponse>({
    queryKey: ['completions', familyId, 'mine', childId],
    queryFn: () => get<CompletionsResponse>(`/families/${familyId}/completions/mine?limit=100`),
    enabled: Boolean(familyId && role === 'child'),
  });

  const { data: membersData } = useQuery<FamilyMembersResponse>({
    queryKey: ['members', familyId],
    queryFn: () => get<FamilyMembersResponse>(`/families/${familyId}/members`),
    enabled: Boolean(familyId),
  });

  // Mutations
  const markCompleteMutation = useMutation({
    mutationFn: (choreId: string) =>
      post<void>(`/families/${familyId}/chores/${choreId}/complete`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['completions', familyId] }),
  });

  const archiveMutation = useMutation({
    mutationFn: (choreId: string) =>
      del<void>(`/families/${familyId}/chores/${choreId}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['chores', familyId] }),
  });

  const members = membersData?.members ?? [];
  const children = members.filter((m) => m.role === 'child');
  const allChores = choresData?.chores ?? [];
  const completions = completionsData?.completions ?? [];

  // For parent view
  const activeChores = allChores.filter((c) => c.isActive && !c.isArchived);

  // For child today view (from /chores/today — schedule-aware + completion-aware)
  const todayChores = todayData?.chores ?? [];
  const todayCompletions = todayData?.completions ?? [];

  const getTodayCompletions = (choreId: string): ChoreCompletion[] =>
    todayCompletions.filter((c) => c.choreId === choreId);
  const filteredChores =
    filterChildId === 'all'
      ? activeChores
      : activeChores.filter((c) => c.assignedTo === filterChildId || c.assignedTo === 'any');

  // Tab definitions
  const parentTabs = [
    { id: 'active' as const, label: 'Active' },
    { id: 'all' as const, label: 'All Chores' },
  ];
  const childTabs = [
    { id: 'today' as const, label: "Today's Chores" },
    { id: 'history' as const, label: 'History' },
  ];
  const tabs = role === 'child' ? childTabs : parentTabs;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <CheckSquare size={22} className="text-primary-600" aria-hidden="true" />
            <h1 className="text-xl font-bold text-slate-900">Chores</h1>
          </div>
          {role === 'parent' && (
            <Button
              onClick={() => setShowAddModal(true)}
              leftIcon={<Plus size={16} />}
            >
              Add Chore
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`tabpanel-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex-1 py-2.5 text-sm font-medium transition-colors duration-150',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
                activeTab === tab.id
                  ? 'border-b-2 border-primary-600 text-primary-700'
                  : 'border-b-2 border-transparent text-slate-500 hover:text-slate-700',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Parent filter */}
        {role === 'parent' && (activeTab === 'active' || activeTab === 'all') && children.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={14} className="text-slate-400" aria-hidden="true" />
            <span className="text-xs text-slate-500">Filter:</span>
            {[{ memberId: 'all', displayName: 'All children' }, ...children].map((c) => (
              <button
                key={c.memberId}
                type="button"
                onClick={() => setFilterChildId(c.memberId)}
                className={cn(
                  'rounded-full px-3 py-1 text-xs font-medium transition-colors duration-150',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  filterChildId === c.memberId
                    ? 'bg-primary-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50',
                )}
              >
                {c.displayName}
              </button>
            ))}
          </div>
        )}

        {/* Tab panels */}

        {/* Active (parent) */}
        {role === 'parent' && activeTab === 'active' && (
          <div id="tabpanel-active" role="tabpanel" aria-label="Active chores">
            {choresLoading ? (
              <div className="grid gap-4 sm:grid-cols-2">
                {[1, 2, 3, 4].map((i) => (
                  <Card key={i} className="animate-pulse h-32">{null}</Card>
                ))}
              </div>
            ) : filteredChores.length === 0 ? (
              <Card className="flex flex-col items-center gap-3 py-10 text-center">
                <CheckSquare size={32} className="text-slate-300" aria-hidden="true" />
                <p className="text-sm text-slate-500">No active chores. Add one to get started!</p>
              </Card>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {filteredChores.map((chore) => (
                  <ChoreCard
                    key={chore.id}
                    chore={chore}
                    completions={[]}
                    userRole="parent"
                    onMarkComplete={() => undefined}
                    onApprove={() => undefined}
                    onReject={() => undefined}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* All chores (parent) */}
        {role === 'parent' && activeTab === 'all' && (
          <div id="tabpanel-all" role="tabpanel" aria-label="All chores">
            {choresLoading ? (
              <div className="grid gap-4 sm:grid-cols-2">
                {[1, 2, 3, 4].map((i) => (
                  <Card key={i} className="animate-pulse h-32">{null}</Card>
                ))}
              </div>
            ) : allChores.length === 0 ? (
              <Card className="flex flex-col items-center gap-3 py-10 text-center">
                <CheckSquare size={32} className="text-slate-300" aria-hidden="true" />
                <p className="text-sm text-slate-500">No chores yet.</p>
              </Card>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {allChores.map((chore) => (
                  <div key={chore.id} className="relative">
                    {chore.isArchived && (
                      <Badge variant="neutral" className="absolute top-3 right-3 z-10">Archived</Badge>
                    )}
                    <ChoreCard
                      chore={chore}
                      completions={[]}
                      userRole="parent"
                      onMarkComplete={() => undefined}
                      onApprove={() => undefined}
                      onReject={() => undefined}
                    />
                    <div className="flex gap-2 mt-2 px-1">
                      <button
                        type="button"
                        onClick={() => setEditChore(chore)}
                        className="text-xs text-primary-600 hover:text-primary-800 font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-500 rounded"
                      >
                        Edit
                      </button>
                      {!chore.isArchived && (
                        <button
                          type="button"
                          onClick={() => archiveMutation.mutate(chore.id)}
                          className="text-xs text-danger-600 hover:text-danger-800 font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-danger-500 rounded"
                        >
                          Archive
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Today's chores (child) */}
        {role === 'child' && activeTab === 'today' && (
          <div id="tabpanel-today" role="tabpanel" aria-label="Today's chores">
            {todayLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => <Card key={i} className="animate-pulse h-32">{null}</Card>)}
              </div>
            ) : todayChores.length === 0 ? (
              <Card className="flex flex-col items-center gap-3 py-10 text-center">
                <span className="text-4xl" aria-hidden="true">🎉</span>
                <p className="text-sm font-medium text-slate-600">No chores assigned today!</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {todayChores.map((chore) => (
                  <ChoreCard
                    key={chore.id}
                    chore={chore}
                    completions={getTodayCompletions(chore.id)}
                    userRole="child"
                    onMarkComplete={() => markCompleteMutation.mutate(chore.id)}
                    onApprove={() => undefined}
                    onReject={() => undefined}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* History (child) */}
        {role === 'child' && activeTab === 'history' && (
          <div id="tabpanel-history" role="tabpanel" aria-label="Chore history">
            {completionsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4].map((i) => <Card key={i} className="animate-pulse h-16">{null}</Card>)}
              </div>
            ) : completions.length === 0 ? (
              <Card className="flex flex-col items-center gap-2 py-10 text-center">
                <CheckSquare size={28} className="text-slate-300" aria-hidden="true" />
                <p className="text-sm text-slate-500">No history yet. Complete some chores!</p>
              </Card>
            ) : (
              <div className="space-y-2" role="list">
                {completions.map((c) => (
                  <Card key={c.id} padding="sm" className="flex items-center justify-between gap-2" aria-label="listitem">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 truncate">{c.choreName}</p>
                      <p className="text-xs text-slate-400">{formatDate(c.submittedAt)}</p>
                    </div>
                    <Badge
                      variant={
                        c.status === 'approved' ? 'success' : c.status === 'rejected' ? 'danger' : 'warning'
                      }
                    >
                      {c.status.charAt(0).toUpperCase() + c.status.slice(1)}
                    </Badge>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Add chore modal */}
      <ChoreFormModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        familyId={familyId}
        members={members}
      />

      {/* Edit chore modal */}
      {editChore && (
        <ChoreFormModal
          isOpen={Boolean(editChore)}
          onClose={() => setEditChore(null)}
          choreId={editChore.id}
          initialValues={{
            name: editChore.name,
            description: editChore.description ?? undefined,
            pointValue: editChore.pointValue,
            assignedTo: editChore.assignedTo,
            category: editChore.category ?? undefined,
          }}
          initialSchedule={editChore.schedule}
          familyId={familyId}
          members={members}
        />
      )}
    </div>
  );
}
